import type { CSSProperties, ReactNode } from 'react';

/**
 * A Material Icons glyph.
 *
 * Renders a plain `<span>`. The `material-icons` class is the Google webfont's
 * own hook - it maps the element's text to the font's ligature table - so it
 * stays; `cw-icon` is the portal's styling hook, and the stylesheets that used
 * to select icons by element name now select that class instead.
 *
 * The glyph is the ligature text: `<Icon>expand_more</Icon>`.
 */
export interface IconProps {
  children: ReactNode;
  className?: string;
  title?: string;
  style?: CSSProperties;
  /** Icons are decorative by default; pass a label to expose one to a screen reader. */
  ariaLabel?: string;
}

export function Icon({ children, className, title, style, ariaLabel }: IconProps) {
  const classes = ['cw-icon', 'material-icons', className].filter(Boolean).join(' ');

  return (
    <span
      className={classes}
      title={title}
      style={style}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      translate="no"
    >
      {children}
    </span>
  );
}

export default Icon;
